import { useSelector } from "react-redux";
import Image from "next/image";

import { Button, Card, MealCard } from "@chef/components";
import { BASE_64_1X1_GREY, language, PRODUCT_TYPE_IDS } from "@chef/constants";
import {
  selectDeviationBasket,
  selectDeviationProducts,
  useCalendarQuery,
  usePickAndMixQuery,
  useProductCategoriesQuery,
  useProductsByCategoriesQuery,
} from "@chef/state-management";
import { findFeaturedPortraitImage } from "@chef/state-management/helpers";
import { isPickAndMixProduct } from "@chef/helpers";
import {
  getRecipesFromPickAndMix,
  getStandaloneProductCategoryIdsFromProductCategories,
} from "@chef/state-management/helpers";
import { RecipeTags, ShoppingCart } from "@chef/smart-components";
import { intl } from "./BasketOverview.Intl";

interface BasketOverviewProps {
  week: number;
  year: number;
  onClose: () => void;
  allowMealRemoval?: boolean;
  allowGroceryRemoval?: boolean;
  initiallyExpandMealSection?: boolean;
  initiallyExpandGrocerySection?: boolean;
}

export const BasketOverview = ({
  week,
  year,
  onClose,
  allowMealRemoval,
  allowGroceryRemoval,
  initiallyExpandMealSection,
  initiallyExpandGrocerySection,
}: BasketOverviewProps) => {
  const deviationBasketForCurrentWeekYear = useSelector(
    selectDeviationBasket({ week, year }),
  );

  const { data: calendarData } = useCalendarQuery({
    week,
    year,
    range: 1,
  });

  const { data: pickAndMixData } = usePickAndMixQuery({
    week,
    year,
    productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
  });

  const { data: productCategoriesData } = useProductCategoriesQuery();

  const { data: standaloneProductsByCategoriesData } =
    useProductsByCategoriesQuery(
      {
        categoryIds: getStandaloneProductCategoryIdsFromProductCategories(
          productCategoriesData?.productCategories || [],
        ),
        week,
        year,
      },
      { skip: !productCategoriesData },
    );

  const selectedProducts = useSelector(selectDeviationProducts({ week, year }));

  if (
    !pickAndMixData ||
    !calendarData ||
    !standaloneProductsByCategoriesData ||
    !selectedProducts ||
    !deviationBasketForCurrentWeekYear
  ) {
    return null;
  }

  const selectedPickAndMix = selectedProducts.filter(isPickAndMixProduct);

  const selectedWithRecipes = selectedPickAndMix.map((bp) => ({
    ...bp,
    recipes: getRecipesFromPickAndMix({
      pickAndMix: pickAndMixData.pickAndMix,
      productId: bp.productId,
    }),
  }));

  return (
    <Card
      noPadding
      transparent
      className="flex flex-col items-center h-auto max-w-screen-lg mx-auto"
    >
      <div className="grid w-full h-full grid-cols-1 rounded bg-background lg:grid-cols-3">
        <div className="order-last hidden w-full col-span-2 px-6 pt-8 pb-4 lg:order-first lg:block">
          <h3>
            <strong>{intl.YOUR_ORDER}</strong>
          </h3>

          <div className="grid grid-cols-3 gap-2 pt-6">
            {selectedWithRecipes.map((product) => {
              const recipe = product.recipes?.find(() => true);

              if (!recipe) {
                return null;
              }

              const url = findFeaturedPortraitImage(recipe)?.url;

              return (
                <MealCard
                  hideFooter
                  key={product.productId}
                  Image={() => (
                    <Image
                      src={url || BASE_64_1X1_GREY}
                      alt={recipe.recipeName}
                      width={2}
                      height={3}
                      className="object-cover w-full h-auto aspect-auto"
                      sizes="(max-width: 375px) 35vw, (max-width: 768px) 25vw, 20vw"
                    />
                  )}
                  text={recipe.recipeName}
                  tags={<RecipeTags recipe={recipe} />}
                  language={language}
                />
              );
            })}
          </div>
        </div>

        <div className="order-first col-span-1 p-6 pt-8 lg:order-last md:bg-grey-3 bg-grey-white">
          <h3>
            <strong className="hidden lg:block">{intl.RECEIPT}</strong>
            <strong className="block lg:hidden">{intl.YOUR_ORDER}</strong>
          </h3>

          <ShoppingCart
            deviation={deviationBasketForCurrentWeekYear}
            allowMealRemoval={allowMealRemoval}
            allowGroceryRemoval={allowGroceryRemoval}
            initiallyExpandMealSection={initiallyExpandMealSection}
            initiallyExpandGrocerySection={initiallyExpandGrocerySection}
            showMealImages
          />

          <Button outlined full className="mt-6" onClick={onClose}>
            {intl.CLOSE}
          </Button>
        </div>
      </div>
    </Card>
  );
};
